import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ChatCircleDots, ShieldCheck, PaperPlaneRight } from "@phosphor-icons/react";

const STATUS_BADGE = {
  Open: "bg-warning/10 text-warning border-warning/20",
  Reviewed: "bg-primary/10 text-primary border-primary/20",
  Resolved: "bg-success/10 text-success border-success/20",
};

export default function InquiriesInbox() {
  const [tab, setTab] = useState("Open");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payroll/inquiries", { params: { status: tab === "All" ? undefined : tab } });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="inquiries-page">
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center shrink-0">
          <ChatCircleDots weight="duotone" className="h-6 w-6 text-primary-foreground" />
        </div>
        <div>
          <p className="uppercase text-xs tracking-[0.2em] font-medium text-muted-foreground">Payroll</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Inquiries Inbox</h1>
          <p className="text-muted-foreground mt-2 text-sm">All payroll inquiries from employees, in one queue.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="inquiries-tabs">
          <TabsTrigger value="Open" data-testid="tab-open">Open</TabsTrigger>
          <TabsTrigger value="Reviewed" data-testid="tab-reviewed">Reviewed</TabsTrigger>
          <TabsTrigger value="Resolved" data-testid="tab-resolved">Resolved</TabsTrigger>
          <TabsTrigger value="All" data-testid="tab-all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <p className="text-center py-12 text-muted-foreground">Loading...</p>
      ) : items.length === 0 ? (
        <Card className="p-12 border-border shadow-none text-center">
          <ChatCircleDots weight="duotone" className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground">No {tab === "All" ? "" : tab.toLowerCase()} inquiries.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((c) => <InquiryCard key={c.id} c={c} onDone={load} />)}
        </div>
      )}
    </div>
  );
}

function InquiryCard({ c, onDone }) {
  const [resp, setResp] = useState(c.admin_response || "");
  const [status, setStatus] = useState(c.status === "Open" ? "Reviewed" : c.status);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/payroll/comments/${c.id}`, { status, admin_response: resp });
      toast.success("Response sent");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-5 border-border shadow-none animate-fade-up" data-testid={`inquiry-${c.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-semibold">{c.employee_name}</p>
          <p className="text-xs text-muted-foreground">
            Pay period: {c.pay_period_start || "—"} → {c.pay_period_end || "—"} · {new Date(c.date_submitted).toLocaleString()}
          </p>
        </div>
        <Badge variant="outline" className={STATUS_BADGE[c.status]}>{c.status}</Badge>
      </div>

      <p className="text-sm mt-3 rounded-lg bg-muted/50 border border-border p-3">{c.comment_text}</p>

      {c.admin_response && (
        <div className="mt-3 pl-3 border-l-2 border-primary/40">
          <p className="text-xs uppercase tracking-wide text-primary mb-0.5 flex items-center gap-1">
            <ShieldCheck className="h-3 w-3" /> HR response {c.responder_name ? `· ${c.responder_name}` : ""}
          </p>
          <p className="text-sm">{c.admin_response}</p>
        </div>
      )}

      <div className="mt-4 space-y-2">
        <Textarea data-testid={`inquiry-response-${c.id}`} value={resp} onChange={(e) => setResp(e.target.value)}
          placeholder="Write a reply to this inquiry..." className="min-h-[44px]" />
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-9 w-36" data-testid={`inquiry-status-${c.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Open">Open</SelectItem>
              <SelectItem value="Reviewed">Reviewed</SelectItem>
              <SelectItem value="Resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={save} disabled={busy} data-testid={`send-inquiry-${c.id}`} className="gap-1.5 font-semibold">
            <PaperPlaneRight className="h-4 w-4" /> {busy ? "Sending..." : "Send reply"}
          </Button>
        </div>
      </div>
    </Card>
  );
}
